import { Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { OrientationsService } from 'src/app/travelxnew/orientations.service';
import * as OrientationActions from 'src/app/travelxnew/store/actions/orientations.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { TravelXNewState } from 'src/app/travelxnew/store/state/travelxnew.state';
import { Store } from '@ngrx/store';
import {
    getExpandedSectionState,
    getSelectedContract,
    selectCompliance,
    selectOverview,
    selectPayroll,
    selectHousing,
    selectBenefit,
    selectFacility
} from 'src/app/travelxnew/store/selectors/orientations.selectors';
import { OrientationSection } from 'src/app/travelxnew/models/orientation-profile/orientation-section.enum';

@Injectable()
export class OrientationsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _service: OrientationsService,
        private readonly _store: Store<TravelXNewState>
    ) {}

    getOrientationContracts$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                OrientationActions.getOrientationContractsByCandidateId,
                OrientationActions.updateOrientationStatusSuccess
            ),
            switchMap((action) => {
                return this._service.getOrientationContractsByCandidateId(action.candidateId).pipe(
                    map((result) =>
                        OrientationActions.getOrientationContractsByCandidateIdSuccess({
                            contracts: result,
                            candidateId: action.candidateId
                        })
                    ),
                    catchError((error) =>
                        of(
                            OrientationActions.getOrientationContractsByCandidateIdFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        )
    );

    getOrientationContractsByCandidateIdSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationContractsByCandidateIdSuccess),
            concatLatestFrom(() => this._store.select(getSelectedContract)),
            map(([action, selectedContract]) => {
                if (selectedContract && selectedContract.candidateId === action.candidateId) {
                    return OrientationActions.setSelectedContract({
                        contractId: selectedContract.id
                    });
                }
                return OrientationActions.setSelectedContract({
                    contractId: action.contracts.length ? action.contracts[0].id : null
                });
            })
        )
    );

    updateOrientationStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.updateOrientationStatus),
            switchMap((action) => {
                const contractId = action.contractId;
                return this._service.updateOrientationStatus(contractId, action.statusId).pipe(
                    map(() =>
                        OrientationActions.updateOrientationStatusSuccess({
                            message: 'Successfully updated orientation status',
                            candidateId: action.candidateId
                        })
                    ),
                    catchError((error) =>
                        of(
                            OrientationActions.updateOrientationStatusFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        )
    );

    getOrientationSectionData$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.setSectionExpandedState),
            concatLatestFrom(() => this._store.select(getSelectedContract)),
            map(([action, selectedContract]) => {
                if (action.state) {
                    switch (action.section) {
                        case OrientationSection.Overview:
                            return OrientationActions.getOrientationOverview({ contractId: selectedContract.id });
                        case OrientationSection.Compliance:
                            return OrientationActions.getOrientationCompliance({ contractId: selectedContract.id });
                        case OrientationSection.Payroll:
                            return OrientationActions.getOrientationPayroll({ contractId: selectedContract.id });
                        case OrientationSection.Housing:
                            return OrientationActions.getOrientationHousing({ contractId: selectedContract.id });
                        case OrientationSection.Benefits:
                            return OrientationActions.getOrientationBenefits({ contractId: selectedContract.id });
                        case OrientationSection.Facility:
                            return OrientationActions.getOrientationFacility({ contractId: selectedContract.id });
                    }
                }
                return OrientationActions.noopAction();
            })
        )
    );

    setSelectedContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.setSelectedContract),
            switchMap((action) => [
                OrientationActions.getOrientationOverview({ contractId: action.contractId }),
                OrientationActions.getOrientationCompliance({ contractId: action.contractId }),
                OrientationActions.getOrientationPayroll({ contractId: action.contractId }),
                OrientationActions.getOrientationHousing({ contractId: action.contractId }),
                OrientationActions.getOrientationBenefits({ contractId: action.contractId }),
                OrientationActions.getOrientationFacility({ contractId: action.contractId })
            ])
        )
    );

    getOrientationOverview$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationOverview),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectOverview)]),
            switchMap(([action, expandedSectionState, currentOverview]) => {
                if (expandedSectionState[OrientationSection.Overview]) {
                    if (currentOverview?.id === action.contractId) {
                        return of(
                            OrientationActions.getOrientationOverviewSuccess({
                                overview: currentOverview
                            })
                        );
                    }
                    return this._service.getOrientationOverview(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationOverviewSuccess({
                                overview: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationOverviewFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    getOrientationCompliance$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationCompliance),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectCompliance)]),
            switchMap(([action, expandedSectionState, currentCompliance]) => {
                if (expandedSectionState[OrientationSection.Compliance]) {
                    if (currentCompliance?.id === action.contractId) {
                        return of(
                            OrientationActions.getOrientationComplianceSuccess({
                                compliance: currentCompliance
                            })
                        );
                    }
                    return this._service.getOrientationCompliance(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationComplianceSuccess({
                                compliance: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationComplianceFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    getOrientationPayroll$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationPayroll),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectPayroll)]),
            switchMap(([action, expandedSectionState, currentPayroll]) => {
                if (expandedSectionState[OrientationSection.Payroll]) {
                    if (currentPayroll?.id === action.contractId) {
                        return of(
                            OrientationActions.getOrientationPayrollSuccess({
                                payroll: currentPayroll
                            })
                        );
                    }
                    return this._service.getOrientationPayroll(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationPayrollSuccess({
                                payroll: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationPayrollFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    getOrientationHousing$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationHousing),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectHousing)]),
            switchMap(([action, expandedSectionState, currentHousing]) => {
                if (expandedSectionState[OrientationSection.Housing]) {
                    if (currentHousing?.contractId === action.contractId) {
                        return of(
                            OrientationActions.getOrientationHousingSuccess({
                                housing: currentHousing
                            })
                        );
                    }
                    return this._service.getOrientationHousing(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationHousingSuccess({
                                housing: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationHousingFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    getOrientationBenefit$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationBenefits),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectBenefit)]),
            switchMap(([action, expandedSectionState, currentBenefit]) => {
                if (expandedSectionState[OrientationSection.Benefits]) {
                    if (currentBenefit?.contractId === action.contractId) {
                        return of(
                            OrientationActions.getOrientationBenefitsSuccess({
                                benefit: currentBenefit
                            })
                        );
                    }
                    return this._service.getOrientationBenefits(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationBenefitsSuccess({
                                benefit: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationBenefitsFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    getOrientationFacility$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.getOrientationFacility),
            concatLatestFrom(() => [this._store.select(getExpandedSectionState), this._store.select(selectFacility)]),
            switchMap(([action, expandedSectionState, currentFacility]) => {
                if (expandedSectionState[OrientationSection.Facility]) {
                    if (currentFacility?.contractId === action.contractId) {
                        return of(
                            OrientationActions.getOrientationFacilitySuccess({
                                facility: currentFacility
                            })
                        );
                    }
                    return this._service.getOrientationFacility(action.contractId).pipe(
                        map((result) =>
                            OrientationActions.getOrientationFacilitySuccess({
                                facility: result
                            })
                        ),
                        catchError((error) =>
                            of(
                                OrientationActions.getOrientationFacilityFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(OrientationActions.noopAction());
                }
            })
        )
    );

    orientationSuccessToaster$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OrientationActions.updateOrientationStatusSuccess),
            map((action) => showToasterMessage({ message: action.message }))
        )
    );

    orientationFailureToaster$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                OrientationActions.getOrientationContractsByCandidateIdFailure,
                OrientationActions.updateOrientationStatusFailure
            ),
            map((action) => showToasterMessage({ message: action.error }))
        )
    );
}
